import type { InfiniteData } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";

import { isNullish } from "@core/foundation/types";
import type { CursorPaginatedResponse } from "@core/ports/http";

import type { GenerationPreview } from "@apis/generation/api";
import type { Generation } from "@apis/generation/models";

import { useQueries } from "../../../shared/hooks/useQueries.ts";

type GenerationsData = InfiniteData<CursorPaginatedResponse<GenerationPreview>>;

// oxlint-disable-next-line @typescript-eslint/explicit-function-return-type
export function useGenerationActions() {
  const queryClient = useQueryClient();
  const { getGenerationsQueryOptions } = useQueries();
  const updateGeneration = (generationPreview: GenerationPreview): void => {
    const generationsQueryOptions = getGenerationsQueryOptions({
      order: "desc",
    });

    queryClient.setQueryData<GenerationsData>(
      generationsQueryOptions.infinite.queryKey,
      data => patchGenerations(data, generationPreview),
    );
  };

  return { updateGeneration };
}

function getStatusRank(status: Generation["status"]): number {
  switch (status) {
    case "completed":
    case "failed":
      return 2;
    case "pending":
      return 0;
    case "processing":
      return 1;
  }
}

function patchGenerations(
  data: GenerationsData | undefined,
  generationPreview: GenerationPreview,
): GenerationsData | undefined {
  const { id, status } = generationPreview.generation;

  if (isNullish(data)) {
    return data;
  }

  const cached = data.pages
    .flatMap(page => page.items)
    .find(item => item.generation.id === id);

  if (isNullish(cached)) {
    const [firstPage, ...otherPages] = data.pages;

    if (isNullish(firstPage)) {
      return data;
    }

    return {
      ...data,
      pages: [
        { ...firstPage, items: [generationPreview, ...firstPage.items] },
        ...otherPages,
      ],
    };
  }

  if (getStatusRank(status) < getStatusRank(cached.generation.status)) {
    return data;
  }

  return {
    ...data,
    pages: data.pages.map(page => {
      return {
        ...page,
        items: page.items.map(item =>
          item.generation.id === id ? generationPreview : item,
        ),
      };
    }),
  };
}
